import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type { ShopSettingsBody } from '@/shared/api/generated/model';
import {
  shopOrder,
  shopRefund,
  shopSetStock,
  shopStock,
  shopVariants,
} from '@/shared/api/generated/shop/shop';
import { ApiError } from '@/shared/api/http';
import { describeWhen } from '@/shared/lib/dates';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Dialog, DialogContent } from '@/shared/ui/Dialog';
import { Input } from '@/shared/ui/Input';
import { Switch } from '@/shared/ui/Switch';
import { toast } from '@/shared/ui/Toast';

import styles from './ShopPage.module.scss';
import { money, PAYMENT_METHOD_LABEL, PAYMENT_STATUS, PROVIDER } from './shop';
import { ReturnsBlock } from './ReturnsBlock';
import { VariantsPanel } from './VariantsPanel';

export interface ShopRecordPanelProps {
  recordId: string;
  typeId: string;
  shop: ShopSettingsBody | undefined;
  canWrite: boolean;
  canPublish: boolean;
}

/**
 * Магазин в записи: у товара — остаток, у заказа — номер, сумма, оплата и
 * возврат денег. Остаток правится сразу, без публикации: его меняет и каждый
 * заказ. У прочих записей панели нет.
 */
export function ShopRecordPanel({
  recordId,
  typeId,
  shop,
  canWrite,
  canPublish,
}: ShopRecordPanelProps) {
  if (shop === undefined) {
    return null;
  }

  if (typeId === shop.products?.id) {
    return <ProductPanel recordId={recordId} canWrite={canWrite} />;
  }

  if (typeId === shop.orders?.id) {
    return <OrderPanel recordId={recordId} canRefund={canPublish} />;
  }

  return null;
}

/**
 * Товар: варианты, а без них — остаток самого товара. У товара с вариантами
 * остаток свой у каждого варианта, общий не используется.
 */
function ProductPanel({ recordId, canWrite }: { recordId: string; canWrite: boolean }) {
  const variants = useQuery({
    queryKey: ['variants', recordId],
    queryFn: async () => {
      const response = await shopVariants(recordId);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос вариантов');
      }

      return response.data;
    },
  });
  const [has, setHas] = useState<boolean>();

  if (variants.data === undefined) {
    return null;
  }

  const hasVariants = has ?? (variants.data.variants ?? []).length > 0;

  return (
    <>
      {!hasVariants && <StockPanel recordId={recordId} canWrite={canWrite} />}
      <VariantsPanel recordId={recordId} canWrite={canWrite} onChanged={setHas} />
    </>
  );
}

function StockPanel({ recordId, canWrite }: { recordId: string; canWrite: boolean }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<string>();
  const [busy, setBusy] = useState(false);

  const stock = useQuery({
    queryKey: ['stock', recordId],
    queryFn: async () => {
      const response = await shopStock(recordId);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос остатка');
      }

      return response.data;
    },
  });

  if (stock.data === undefined) {
    return null;
  }

  const tracked = stock.data.tracked;
  const value = draft ?? String(stock.data.quantity);

  const save = (quantity: number | null) => {
    setBusy(true);

    shopSetStock(recordId, { quantity })
      .then((response) => {
        if (response.status === 200) {
          queryClient.setQueryData(['stock', recordId], response.data);
        }

        setDraft(undefined);
        toast.success(quantity === null ? 'Остаток больше не считается' : 'Остаток сохранён');
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof ApiError ? cause.detail : 'Не удалось сохранить остаток');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const parsed = Number(value);
  const valid = value.trim() !== '' && Number.isInteger(parsed) && parsed >= 0;

  return (
    <section className={styles['panel']} aria-label="Остаток товара">
      <div className={styles['panel__head']}>
        <h2 className={styles['panel__title']}>Остаток</h2>
        {tracked ? (
          <Badge size="sm" tone={stock.data.quantity === 0 ? 'danger' : 'neutral'}>
            {stock.data.quantity === 0 ? 'нет в наличии' : `${String(stock.data.quantity)} шт.`}
          </Badge>
        ) : (
          <Badge size="sm" tone="neutral">
            не считается
          </Badge>
        )}
        {stock.data.waiting > 0 && (
          <Badge size="sm" tone="accent">
            ждут поступления: {stock.data.waiting}
          </Badge>
        )}
        {canWrite && (
          <div className={styles['panel__end']}>
            <Switch
              checked={tracked}
              disabled={busy}
              onCheckedChange={(next) => {
                save(next ? 0 : null);
              }}
            >
              Считать остаток
            </Switch>
          </div>
        )}
      </div>

      {tracked && canWrite && (
        <div className={styles['stock']}>
          <Input
            inputMode="numeric"
            aria-label="Сколько есть"
            value={value}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
          />
          <Button
            size="sm"
            variant="outline"
            loading={busy}
            disabled={!valid || draft === undefined}
            onClick={() => {
              save(parsed);
            }}
          >
            Сохранить
          </Button>
        </div>
      )}

      <p className={styles['muted']}>
        {tracked
          ? 'Сохраняется сразу, без публикации. Отложенное под неоплаченные заказы уже вычтено — вернётся, если заказ отменят.'
          : 'Сколько угодно: заказ не проверяет наличие. Включите, чтобы заказать больше, чем есть, было нельзя.'}
      </p>
    </section>
  );
}

function OrderPanel({ recordId, canRefund }: { recordId: string; canRefund: boolean }) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const order = useQuery({
    queryKey: ['shop-order', recordId],
    queryFn: async () => {
      const response = await shopOrder(recordId);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос заказа');
      }

      return response.data;
    },
  });

  if (order.data === undefined) {
    return null;
  }

  const o = order.data;
  const state =
    o.paid_at !== undefined && o.released
      ? 'refunded'
      : o.paid_at !== undefined
        ? 'paid'
        : o.released
          ? 'cancelled'
          : 'awaiting';

  const refund = () => {
    setBusy(true);

    shopRefund(recordId)
      .then((response) => {
        if (response.status === 200) {
          queryClient.setQueryData(['shop-order', recordId], response.data);
        }

        void queryClient.invalidateQueries({ queryKey: ['record', recordId] });
        setConfirming(false);
        toast.success('Деньги возвращены, товар — на складе');
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof ApiError ? cause.detail : 'Не удалось вернуть деньги');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <section className={styles['panel']} aria-label="Заказ">
      <div className={styles['panel__head']}>
        <h2 className={styles['panel__title']}>
          Заказ №{o.number} · {money(o.amount, o.currency)}
        </h2>
        <Badge size="sm" tone={ORDER_STATE[state].tone}>
          {ORDER_STATE[state].label}
        </Badge>
        {o.guest && (
          <Badge size="sm" tone="neutral">
            без входа
          </Badge>
        )}
        {o.payment_method !== 'online' && (
          <Badge size="sm" tone="accent">
            {PAYMENT_METHOD_LABEL[o.payment_method] ?? o.payment_method}
          </Badge>
        )}
        {canRefund && o.refundable && (
          <div className={styles['panel__end']}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setConfirming(true);
              }}
            >
              Вернуть деньги
            </Button>
          </div>
        )}
      </div>

      <p className={styles['muted']}>
        {o.email}
        {o.expires_at !== undefined &&
          (o.payment_method === 'on_delivery'
            ? ' · оплата при получении — отметьте «оплачен» переходом статуса'
            : ` · ждёт оплаты, отменится сам ${describeWhen(o.expires_at)}`)}
        {o.paid_at !== undefined && ` · оплачен ${describeWhen(o.paid_at)}`}
      </p>

      {(o.payments ?? []).length > 0 && (
        <ul className={styles['payments']}>
          {(o.payments ?? []).map((p) => (
            <li key={p.id} className={styles['payment']}>
              <Badge size="sm" tone={PAYMENT_STATUS[p.status].tone}>
                {PAYMENT_STATUS[p.status].label}
              </Badge>
              {PROVIDER[p.provider]} · {money(p.amount, o.currency)} · {describeWhen(p.created_at)}
              {p.detail !== undefined && p.detail !== '' && ` · ${p.detail}`}
            </li>
          ))}
        </ul>
      )}

      <ReturnsBlock recordId={recordId} order={o} canRefund={canRefund} />

      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent
          title={`Вернуть ${money(Math.max(o.amount - o.refunded, 0), o.currency)}?`}
          footer={
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  setConfirming(false);
                }}
              >
                Не возвращать
              </Button>
              <Button tone="danger" loading={busy} onClick={refund}>
                Вернуть деньги
              </Button>
            </>
          }
        >
          <p className={styles['muted']}>
            {o.refunded > 0
              ? `Часть уже вернули (${money(o.refunded, o.currency)}) — вернётся остаток. `
              : 'Деньги вернутся покупателю целиком. '}
            Тем же путём, каким пришли. Заказ перейдёт в «отменён», оставшийся товар — на склад.
            Отменить возврат нельзя.
          </p>
        </DialogContent>
      </Dialog>
    </section>
  );
}

const ORDER_STATE: Record<
  'awaiting' | 'paid' | 'cancelled' | 'refunded',
  { label: string; tone: 'warning' | 'success' | 'neutral' | 'danger' }
> = {
  awaiting: { label: 'ждёт оплаты', tone: 'warning' },
  paid: { label: 'оплачен', tone: 'success' },
  cancelled: { label: 'отменён, товар на складе', tone: 'neutral' },
  refunded: { label: 'деньги возвращены', tone: 'danger' },
};
